import { sparkAssetUrl } from '../utils/sparkAssetUrl';

export type IllustrationEntry = {
  name: string;
  slug: string;
  src: string;
};

export type IllustrationGroup = {
  name: string;
  illustrations: IllustrationEntry[];
};

function illustration(
  name: string,
  slug: string,
  ext: 'svg' | 'png' = 'svg',
): IllustrationEntry {
  return {
    name,
    slug,
    src: sparkAssetUrl(`illustrations/${slug}.${ext}`),
  };
}

export const illustrationGroups: IllustrationGroup[] = [
  {
    name: 'Box',
    illustrations: [
      illustration('Box open', 'illustration-box-open'),
      illustration('Box closed', 'illustration-box-closed'),
      illustration('Empty state box (medium)', 'illustration-box-open'),
      illustration('Empty state box (small)', 'illustration-box-open'),
    ],
  },
  {
    name: 'Books',
    illustrations: [
      illustration('Book', 'illustration-book'),
      illustration('Book 2', 'illustration-book-2'),
      illustration('Books', 'illustration-books'),
    ],
  },
  {
    name: 'Bottle',
    illustrations: [
      illustration('Bottle large', 'illustration-bottle-large'),
      illustration('Bottle small', 'illustration-bottle-small'),
    ],
  },
  {
    name: 'Tokens & objects',
    illustrations: [
      illustration('Tokens', 'illustration-tokens'),
      illustration('Tokens 2', 'illustration-tokens-2'),
      illustration('Cards', 'illustration-cards'),
      illustration('Candle', 'illustration-candle'),
      illustration('Cylinder', 'illustration-cylinder'),
      illustration('Paper', 'illustration-paper'),
    ],
  },
  {
    name: 'Magic',
    illustrations: [
      illustration('Magic stick and AI', 'illustration-magic-stick-and-ai'),
      illustration('Magic stick and key', 'illustration-magic-stick-and-key'),
    ],
  },
  {
    // Astronaut-helmet sentiment faces for the "Share your thoughts" feedback
    // modal (Figma 9617:25041). Each face ships as two fixed-colour drawings:
    // a grey outline (unselected) and a blue filled version (selected). See
    // `SentimentRating`, which swaps between them per state.
    name: 'Feedback faces',
    illustrations: [
      illustration('Feedback face sad (outline)', 'illustration-feedback-face-sad-outline'),
      illustration('Feedback face sad (filled)', 'illustration-feedback-face-sad-filled'),
      illustration('Feedback face neutral (outline)', 'illustration-feedback-face-neutral-outline'),
      illustration('Feedback face neutral (filled)', 'illustration-feedback-face-neutral-filled'),
      illustration('Feedback face love (outline)', 'illustration-feedback-face-love-outline'),
      illustration('Feedback face love (filled)', 'illustration-feedback-face-love-filled'),
    ],
  },
  {
    name: 'Upsell',
    illustrations: [illustration('Credit usage upgrade', 'credit-usage-upgrade')],
  },
  {
    // Empty-state illustrations exported from Figma MVP-DESIGNES:
    // API keys 9939:17542, training stopped 9939:17613, evaluation failed 9939:17650,
    // no results 9884:32927 (Projects "No matches" state, 9750:36409).
    name: 'Empty states',
    illustrations: [
      illustration('API keys', 'illustration-api-keys'),
      illustration('Training stopped', 'illustration-training-stopped'),
      illustration('Evaluation failed', 'illustration-evaluation-failed'),
      illustration('No results', 'illustration-no-results'),
    ],
  },
  {
    // Playground no-model card (Figma Playground 1806:104275, group 2291:99805): the two
    // racers that overlap the top-left of the card. Exported as one 379×206 SVG.
    name: 'Playground',
    illustrations: [illustration('Playground racers', 'illustration-playground-racers')],
  },
];

/**
 * A registered illustration slug (e.g. `illustration-box-open`, `credit-usage-upgrade`).
 * Slugs are plain strings — the manifest above is the source of truth.
 */
export type IllustrationSlug = string;

/** Resolve an illustration by slug (or pass through a full URL / path). */
export function illustrationSrc(slugOrSrc: string): string {
  for (const group of illustrationGroups) {
    const match = group.illustrations.find((entry) => entry.slug === slugOrSrc);
    if (match) return match.src;
  }
  if (slugOrSrc.includes('/') || slugOrSrc.includes('.')) return slugOrSrc;
  return sparkAssetUrl(`illustrations/${slugOrSrc}.svg`);
}
